import { Building2, GraduationCap, ShieldCheck } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import GetStartedCard from './GetStartedCard';
import HeroSection from './HeroSection';
import Layout from './Layout';
import { Button } from './ui/button';

export default function TrainingPlatformPage() {
  return (
    <div>
      <Layout>
        <div className="space-y-32 pb-20">
          <HeroSection
            title="Build & Deliver Engaging Cybersecurity Training Programs"
            description="Create hands-on cybersecurity courses with real-world exercises. Combine theory with practical labs, gamification, and access to a library of ready-to-use scenarios."
            image="https://images.simulationslabs.com/website+articles/training-hero.png"
            buttons={[
              {
                text: 'Get Started for Free',
                link: 'https://app.simulationslabs.com/register/tenant',
                variant: 'default',
              },
              {
                text: 'Request a Demo',
                link: 'mailto:info@simulationslabs.com',
                variant: 'outline',
              },
            ]}
          />

          <div className="flex space-y-10">
            <div className="flex flex-col gap-4 space-y-32">
              <div className="flex flex-col gap-4 text-center">
                <span className="text-lightBlue text-[15px] font-bold uppercase">Features</span>
                <h1 className="text-[50px] font-medium">
                  Platform Built for Delivering Practical Cybersecurity Training
                </h1>
                <p className="mx-auto max-w-3xl text-gray-500">
                  The Course Builder allows instructors and training teams to create structured
                  programs that combine learning content with hands-on challenges and simulations.
                </p>
              </div>

              <div className="items-center justify-between gap-10 md:flex">
                <div className="flex flex-col gap-4">
                  <img
                    src="https://images.simulationslabs.com/website+articles/easy-course-builder.png"
                    alt="Easy Course Builder"
                    className="md:w-[560px]"
                  />
                </div>
                <div className="flex flex-col gap-4 md:w-1/2">
                  <h1 className="text-[42px] font-medium">Easy Course Builder</h1>
                  <Accordion defaultValue="item-1" type="single" collapsible className="w-full">
                    <AccordionItem value="item-1">
                      <AccordionTrigger>Create Engaging Lessons</AccordionTrigger>
                      <AccordionContent>
                        Craft lessons effortlessly and categorize them into units, allowing for a
                        seamless organization of knowledge. Our intuitive interface empowers you to
                        focus on content creation, ensuring your lessons are not only informative
                        but also engaging.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="item-2">
                      <AccordionTrigger>Hands-On Labs</AccordionTrigger>
                      <AccordionContent>
                        Take learning to the next level by seamlessly integrating hands-on labs into
                        your lessons. Provide practical application opportunities that reinforce
                        theoretical knowledge, giving learners a comprehensive understanding of
                        cybersecurity concepts.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="item-3">
                      <AccordionTrigger>Simulations AI Copilot</AccordionTrigger>
                      <AccordionContent>
                        Get access to a large library of ready-made challenges which enables you to
                        create simulations quickly and easily.
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </div>

              <div className="items-center justify-between gap-10 md:flex">
                <div className="flex flex-col gap-4 md:w-1/2">
                  <h1 className="text-[42px] font-medium">Monitor Trainee Progress</h1>
                  <Accordion defaultValue="item-1" type="single" collapsible className="w-full">
                    <AccordionItem value="item-1">
                      <AccordionTrigger>Comprehensive Analytics</AccordionTrigger>
                      <AccordionContent>
                        Dive deep into your trainees' learning journey with our powerful analytics
                        tools. Gain valuable insights into their skill development, track submission
                        history, and pinpoint areas for improvement.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="item-2">
                      <AccordionTrigger>Real-Time Leaderboard</AccordionTrigger>
                      <AccordionContent>
                        Stay connected with your learners through our dynamic real-time leaderboard.
                        Watch as individuals climb the ranks, celebrating achievements and
                        encouraging a collaborative learning environment.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="item-3">
                      <AccordionTrigger>Data Export for In-Depth Reporting</AccordionTrigger>
                      <AccordionContent>
                        Seamlessly export comprehensive data and generate detailed reports. Exported
                        reports can be used for in-depth analysis, sharing insights with
                        stakeholders, and facilitating data-driven decision-making beyond the
                        platform.
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
                <div className="flex flex-col gap-4">
                  <img
                    src="https://images.simulationslabs.com/website+articles/Monitor-Trainee-Progress.png"
                    alt="Monitor Trainee Progress"
                    className="md:w-[560px]"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid space-y-10">
            <div className="flex flex-col gap-4 text-center">
              <h1 className="text-[42px] font-medium">Built For Security Educators</h1>
              <p className="text-gray-500">
                Trusted by universities, training providers, and corporate security teams
              </p>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              <GetStartedCard
                icon={<GraduationCap size={30} />}
                title="Universities & Academic Programs"
                description="Build comprehensive cybersecurity curricula with practical exercises. Provide students with hands-on experience that complements theoretical coursework and prepares them for real-world challenges."
              />
              <GetStartedCard
                icon={<Building2 size={30} />}
                title="Corporate Security Training"
                description="Train your security teams with custom scenarios tailored to your organization's specific needs. Keep your team's skills sharp with regular practice and updated threat simulations."
              />
              <GetStartedCard
                icon={<ShieldCheck size={30} />}
                title="Training Providers & Bootcamps"
                description="Create professional certification courses and bootcamp programs. Deliver high-quality training that stands out with practical, hands-on exercises and real-world scenarios."
              />
            </div>
          </div>

          <div className="items-center justify-between gap-10 md:flex">
            <div className="grid space-y-10">
              <div className="text-center">
                <h1 className="text-[42px] font-medium">How to get started?</h1>
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <GetStartedCard
                  number="01"
                  title="Create"
                  description="Design hand-on cybersecurity courses tailored to your participants' needs using our user-friendly no-code platform."
                />
                <GetStartedCard
                  number="02"
                  title="Publish"
                  description="Easily launch and share your event with students or teams through the platform."
                />
                <GetStartedCard
                  number="03"
                  title="Monitor"
                  description="Utilize our dashboard to track participants' progress and analyze results throughout and after the training event."
                />
              </div>
              <Button
                onClick={() =>
                  (window.location.href = 'https://app.simulationslabs.com/register/tenant')
                }
                className="mx-auto w-fit"
              >
                Get Started for Free
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    </div>
  );
}
